/* eslint-disable import/prefer-default-export */
import { useRouter } from 'next/router';
import { emptyList, emptyObject } from '@constants/empty';
import { getDateMonthString, getFullDate } from '@utils/dates';
import { differenceInSeconds, differenceInMinutes } from 'date-fns';
import { CARD_STATE, CARD_TYPE } from './constants';

export const savedObjectTypes = {
  LESSON: 1,
  TEST: 2
};

export const getCardState = (isLive, startTime) => {
  if (isLive) {
    return CARD_STATE.LIVE;
  }
  const now = Date.now();
  if (now > startTime) {
    return CARD_STATE.POST_LIVE;
  }
  return CARD_STATE.UPCOMING;
};

export const getSecondsDiff = (startTime) => {
  const diff = differenceInSeconds(startTime, Date.now(), {
    roundingMethod: 'round'
  });

  return diff;
};

export const getMinsDiff = (startTime) => {
  const diff = differenceInMinutes(Date.now(), startTime, {
    roundingMethod: 'round'
  });

  return diff;
};

const getCardsByDate = (cardsList = emptyList, dateMapMutate = {}) => {
  const newDateMap = { ...dateMapMutate };

  for (let i = 0; i < cardsList.length; i += 1) {
    const card = cardsList[i];
    const isFeatured = !!card?.isFeatured;
    const { startTime } = card?.value ?? emptyObject;
    const dateStr = getDateMonthString(startTime, false, false);
    const mapKey = `${getFullDate(startTime)}-${isFeatured}`;

    if (newDateMap[mapKey]) {
      newDateMap[mapKey] = {
        ...newDateMap[mapKey],
        cards: [...newDateMap[mapKey].cards, card]
      };
    } else {
      const nextOrder = newDateMap.lastOrder ?? i;
      newDateMap[mapKey] = {
        order: nextOrder + 1,
        cards: [card],
        date: dateStr
      };
      newDateMap.lastOrder = nextOrder + 1;
    }
  }

  const cardsByDateList = Object.keys(newDateMap)
    .filter((key) => key !== 'lastOrder')
    .map((key) => newDateMap[key]);
  cardsByDateList.sort((card1, card2) => card1.order - card2.order);

  return { cardsByDateList, newDateMap };
};

export const processScheduleCardInfo = (
  response = emptyObject,
  initialDateMap
) => {
  const cardsList = [];
  const dateMapMutate = initialDateMap || {};

  if (response.featuredItem) {
    cardsList.push({ ...response.featuredItem, isFeatured: true });
  }

  if (response.results) {
    cardsList.push(...response.results);
  }

  const { cardsByDateList: cardsByDate, newDateMap: dateMap } = getCardsByDate(
    cardsList,
    dateMapMutate
  );

  return {
    next: response.next,
    cardsByDate,
    dateMap,
    programmeType: response.programmeType
  };
};

export const getShowSaveIcon = ({
  isLoggedIn = false,
  hasSkuAccess = false,
  isSubscribed = false
}) => {
  if (isLoggedIn) {
    return hasSkuAccess || isSubscribed;
  }
  return false;
};

export const getIsCardDisabled = ({
  isLoggedIn = false,
  fromStore = false,
  hasSkuAccess = false,
  isSubscribed = false
}) => {
  if (isLoggedIn) {
    if (fromStore) {
      if (isSubscribed) {
        return false;
      }

      return !hasSkuAccess;
    }
    return !isSubscribed;
  }
  return true;
};

export const useIsFromStore = () => {
  const { route } = useRouter();
  return route.startsWith('/store/');
};

const getSavedContentType = (entityType) => {
  if (entityType === savedObjectTypes.LESSON) return 'Lesson';
  if (entityType === savedObjectTypes.TEST) return 'Test';
  return '';
};

export const getItemTypes = (type) => {
  const isTest = type === CARD_TYPE.QUIZ;
  const entityType = isTest ? savedObjectTypes.TEST : savedObjectTypes.LESSON;
  const contentType = getSavedContentType(entityType);
  return { isTest, entityType, contentType };
};
